/**
 * <trailer-filter> — the saved trailer, kept as a filter on the category pages.
 *
 * A shopper who has identified a trailer (a reg-nr or VIN lookup, saved by
 * assets/trailer-context.js) is shopping for that trailer, not for the catalogue. So a category
 * page that holds parts fitting it says so, and once the filter is on it STAYS on, page after
 * page, until the trailer is forgotten. Åke's call, 2026-09-07.
 *
 * ## How it can be exact without a request
 *
 * The fitment result already fetched a card for every fitting article number, and each card
 * names its collections, so `ValerydTrailer` holds an article-number → collections index in the
 * shopper's own browser. Intersecting it with this page's handle gives the fitting parts IN
 * THIS CATEGORY — the real count, not "how many happen to be on page 1". Products sit in their
 * ancestor categories as well as their leaf, so a parent category matches too.
 *
 * That is the whole reason this replaced its predecessor, which hid the non-matching cards of
 * the page in front of it and counted what was left. On a category of 300 products, page 1 of
 * 13 could show nothing while five fitting parts sat on page 4 — a filter that looks broken
 * exactly when it matters.
 *
 * ## What the filtered view is, deliberately
 *
 * The whole answer, not a page of it. There is nothing left to paginate — a trailer's parts in
 * one category is a handful — so the sort, the facets, the pagination and the store's own count
 * are hidden with the grid rather than left standing describing the unfiltered collection. The
 * bar states the count it is actually showing, and links to the trailer's full parts list.
 *
 * Cards come from the server through ValerydCards.cards(), like every other JS-assembled grid
 * in the theme. Without JS none of this renders and the category page is the plain category
 * page — which is also what happens if the card render fails: the filter takes itself down
 * rather than stand in front of a page it cannot replace.
 */
(function () {
  if (customElements.get('trailer-filter')) return;
  var EVENT = 'trailer:changed';

  customElements.define(
    'trailer-filter',
    class TrailerFilter extends HTMLElement {
      connectedCallback() {
        this.handle = (this.dataset.collection || '').trim();
        this.region = document.querySelector('[data-trailer-hideable]');
        if (!this.handle || !this.region || !window.ValerydTrailer || !window.ValerydCards) return;

        this.onChange = () => this.render();
        document.addEventListener(EVENT, this.onChange);
        this.render();
      }

      disconnectedCallback() {
        if (this.onChange) document.removeEventListener(EVENT, this.onChange);
        if (this.request) this.request.abort();
      }

      render() {
        var saved = window.ValerydTrailer.read();
        var fitting = saved ? window.ValerydTrailer.inCollection(saved, this.handle) : [];
        if (!saved || !fitting.length) {
          // No trailer, or none of its parts live in this category: say nothing at all. A bar
          // reading "0 parts fit" over a category the shopper is browsing anyway is noise, and
          // it would also overclaim — the index knows what EasySearch returned, not what fits.
          this.hidden = true;
          this.replaceChildren();
          return this.reveal();
        }

        var on = window.ValerydTrailer.filtering(saved);
        this.replaceChildren(this.bar(saved, fitting, on));
        this.hidden = false;

        if (!on) return this.reveal();
        this.region.hidden = true;
        this.show(saved, fitting);
      }

      // Put the category page back and take the filtered grid down. Every path that decides
      // not to filter ends here, the failures included.
      reveal() {
        if (this.region) this.region.hidden = false;
        this.rendered = null;
        if (this.grid) {
          this.grid.remove();
          this.grid = null;
        }
      }

      bar(saved, fitting, on) {
        var bar = document.createElement('div');
        bar.className = 'trailer-filter';

        var count = document.createElement('p');
        count.className = 'trailer-filter__count';
        count.textContent = this.fill(on ? 'showing' : 'offer', fitting.length, saved.regnr);
        bar.appendChild(count);

        var actions = document.createElement('div');
        actions.className = 'trailer-filter__actions';

        var toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'btn btn--secondary trailer-filter__toggle';
        toggle.textContent = this.fill(on ? 'showAll' : 'showFitting', fitting.length, saved.regnr);
        toggle.addEventListener('click', function () {
          window.ValerydTrailer.setFiltering(!on);
        });
        actions.appendChild(toggle);

        if (this.dataset.allUrl && this.dataset.allLabel) {
          var all = document.createElement('a');
          all.className = 'trailer-filter__all';
          all.href = this.dataset.allUrl + '?q=' + encodeURIComponent(saved.regnr);
          all.textContent = this.fill('allLabel', fitting.length, saved.regnr);
          actions.appendChild(all);
        }
        bar.appendChild(actions);
        return bar;
      }

      // Count-bearing strings arrive as both plural templates, because the count is only known
      // here — the pattern dealer-count.js and store-availability.js already use.
      fill(key, count, regnr) {
        var other = this.dataset[key] || '';
        var one = this.dataset[key + 'One'] || other;
        return (count === 1 ? one : other).replace('%COUNT%', count).replace('%REGNR%', regnr);
      }

      async show(saved, fitting) {
        // Re-rendering the same set on every 'trailer:changed' would re-fetch the same cards;
        // the grid already standing is the answer.
        var key = fitting.join(',');
        if (this.rendered === key && this.grid) return;
        this.rendered = key;

        if (this.request) this.request.abort();
        this.request = new AbortController();
        var signal = this.request.signal;

        var grid = document.createElement('ul');
        grid.className = 'trailer-filter__grid';
        grid.setAttribute('role', 'list');
        grid.setAttribute('aria-busy', 'true');
        if (this.grid) this.grid.remove();
        this.grid = grid;
        this.after(grid);

        var result;
        try {
          result = await window.ValerydCards.cards(fitting, { signal: signal });
        } catch (error) {
          if (signal.aborted) return;
          return this.reveal();
        }
        if (signal.aborted) return;

        var cards = fitting.map((sku) => result.bySku.get(sku)).filter(Boolean);
        // Nothing resolvable, or the render only half succeeded: the category page as the
        // server sent it is a true page, and half a filtered grid is not.
        if (!cards.length || result.incomplete) return this.reveal();
        grid.setAttribute('aria-busy', 'false');
        // h2, the level the category page's own grid asks for: this grid stands in its place,
        // with only the bar's paragraph between it and the page's h1.
        grid.replaceChildren(...cards.map((li) => window.ValerydCards.atLevel(li.cloneNode(true), 2)));
      }
    }
  );
})();
